/**
 * Feature Registry API Service
 * Fetches features from backend and provides search functionality
 * Version: 6.2.0
 */
import apiClient from './apiClient'

// =============================================================================
// Types
// =============================================================================

export interface Feature {
  id: number
  feature_id: string
  name: string
  description?: string
  feature_type: string
  menu_path?: string
  page_url?: string
  icon?: string
  badge?: string
  color?: string
  permissions?: string
  requires_auth: boolean
  roles?: string
  keywords?: string
  tags?: string
  help_text?: string
  how_to_use?: string
  related_features?: string
  next_steps?: string
  api_endpoint?: string
  api_method?: string
  is_active: boolean
  is_visible: boolean
  is_new: boolean
  show_tour: boolean
  tour_step?: number
  tour_priority: number
  category_id?: number
  children?: Feature[]
}

export interface FeatureCategory {
  id: number
  name: string
  description?: string
  icon?: string
  sort_order: number
  is_active: boolean
}

export interface FeatureSearchResult {
  features: Feature[]
  total: number
  page: number
  page_size: number
}

export interface FeatureTree {
  tree: Feature[]
  total: number
}

// =============================================================================
// API Endpoints
// =============================================================================

const API_PREFIX = '/features'

/**
 * Fetch all features with optional filtering
 */
export async function fetchFeatures(params?: {
  page?: number
  page_size?: number
  category_id?: number
  feature_type?: string
  is_active?: boolean
  show_tour?: boolean
}): Promise<FeatureSearchResult> {
  const response = await apiClient.get<FeatureSearchResult>(`${API_PREFIX}`, { params })
  return response.data
}

/**
 * Search features by query
 */
export async function searchFeatures(
  query: string,
  featureType?: string,
  page: number = 1,
  pageSize: number = 20
): Promise<FeatureSearchResult> {
  const response = await apiClient.get<FeatureSearchResult>(`${API_PREFIX}/search`, {
    params: {
      q: query,
      feature_type: featureType,
      page,
      page_size: pageSize,
    },
  })
  return response.data
}

/**
 * Get feature tree structure
 */
export async function fetchFeatureTree(categoryId?: number): Promise<FeatureTree> {
  const response = await apiClient.get<FeatureTree>(`${API_PREFIX}/tree`, {
    params: { category_id: categoryId },
  })
  return response.data
}

/**
 * Get feature by URL path
 */
export async function fetchFeatureByUrl(url: string): Promise<Feature> {
  const response = await apiClient.get<Feature>(`${API_PREFIX}/by-url/${url}`)
  return response.data
}

/**
 * Get tour features (features marked for guided tour)
 */
export async function fetchTourFeatures(): Promise<{ tour_steps: Feature[]; total_steps: number }> {
  const response = await apiClient.get(`${API_PREFIX}/tour`)
  return response.data
}

/**
 * Get feature by ID
 */
export async function fetchFeatureById(featureId: number): Promise<Feature> {
  const response = await apiClient.get<Feature>(`${API_PREFIX}/${featureId}`)
  return response.data
}

/**
 * Fetch all categories
 */
export async function fetchCategories(includeInactive: boolean = false): Promise<FeatureCategory[]> {
  const response = await apiClient.get<FeatureCategory[]>(`${API_PREFIX}/categories`, {
    params: { include_inactive: includeInactive },
  })
  return response.data
}

/**
 * Get navigation items (features formatted for navigation)
 */
export async function fetchNavigationItems(): Promise<Feature[]> {
  try {
    const tree = await fetchFeatureTree()
    return flattenFeatureTree(tree.tree)
  } catch (error) {
    console.error('Failed to fetch navigation items:', error)
    return []
  }
}

/**
 * Flatten feature tree for navigation
 */
function flattenFeatureTree(features: Feature[], depth: number = 0): Feature[] {
  const result: Feature[] = []
  
  for (const feature of features) {
    result.push({
      ...feature,
      keywords: feature.keywords || '',
      tags: feature.tags || '',
    })
    
    if (feature.children && feature.children.length > 0) {
      result.push(...flattenFeatureTree(feature.children, depth + 1))
    }
  }
  
  return result
}

// =============================================================================
// Local Storage Helpers
// =============================================================================

const RECENT_FEATURES_KEY = 'zobull_recent_features'
const MAX_RECENT_FEATURES = 10

/**
 * Add feature to recent features list
 */
export function addToRecentFeatures(feature: Feature): void {
  const recent = getRecentFeatures()
  
  // Remove if already exists
  const filtered = recent.filter((f) => f.id !== feature.id)
  
  // Add to beginning
  filtered.unshift({
    id: feature.id,
    feature_id: feature.feature_id,
    name: feature.name,
    description: feature.description,
    feature_type: feature.feature_type,
    page_url: feature.page_url,
    icon: feature.icon,
    is_active: feature.is_active,
    is_visible: feature.is_visible,
    is_new: feature.is_new,
    show_tour: feature.show_tour,
    tour_step: feature.tour_step,
    tour_priority: feature.tour_priority,
    requires_auth: feature.requires_auth,
  })
  
  // Keep only MAX_RECENT_FEATURES
  const trimmed = filtered.slice(0, MAX_RECENT_FEATURES)
  
  localStorage.setItem(RECENT_FEATURES_KEY, JSON.stringify(trimmed))
}

/**
 * Get recent features from local storage
 */
export function getRecentFeatures(): Partial<Feature>[] {
  try {
    const stored = localStorage.getItem(RECENT_FEATURES_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

/**
 * Clear recent features
 */
export function clearRecentFeatures(): void {
  localStorage.removeItem(RECENT_FEATURES_KEY)
}

/**
 * Search features locally (offline fallback)
 */
export function searchFeaturesLocally(
  features: Feature[],
  query: string
): Feature[] {
  if (!query.trim()) return features
  
  const lowerQuery = query.toLowerCase()
  
  return features.filter((feature) => {
    const searchableText = [
      feature.name,
      feature.description,
      feature.feature_id,
      feature.keywords,
      feature.tags,
      feature.help_text,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    
    return searchableText.includes(lowerQuery)
  })
}